import type { OnboardingGoal, OnboardingRole, UserOnboarding } from '../../api/userOnboarding';
import type { NavigationGroupKey } from '../../utils/SideBarRoutes';
import { Routes } from '../../enums/Routes';

export const ROLES: OnboardingRole[] = ['GENERAL_MANAGER', 'HR', 'LEADER', 'SAFETY_REP', 'OTHER'];

/** The goals that can be chosen now. Safety rounds and the demo company come later. */
export const GOALS: OnboardingGoal[] = ['TEAM_SAFETY', 'WORK_ENVIRONMENT'];

const GROUPS: Record<OnboardingRole, NavigationGroupKey | null> = {
  LEADER: 'teamleder',
  HR: 'hr',
  GENERAL_MANAGER: 'hr',
  SAFETY_REP: 'verneombud',
  OTHER: null,
};

/** The part of the menu that comes first for the chosen role */
export const groupForRole = (role: OnboardingRole | null): NavigationGroupKey | null => (role ? GROUPS[role] : null);

/** Where the goal is worked on later, which is also where «Kom i gang» lives */
export const homeFor = (goal: OnboardingGoal | null): string | null =>
  goal === 'TEAM_SAFETY' ? Routes.Dashboard : goal === 'WORK_ENVIRONMENT' ? Routes.HRHomeFullPath : null;

/** The first step starts in «Opprett team», with the guidance for the goal */
export const firstStepPath = (goal: OnboardingGoal) => `${Routes.CreateTeam}?onboarding=1&goal=${goal}`;

export type StatusCard = { key: string; tone: 'neutral' | 'info' };

/** Three cards that say what will appear on the page, or show an example */
export const STATUS_CARDS: Partial<Record<OnboardingGoal, string[]>> = {
  TEAM_SAFETY: ['latest', 'ongoing', 'measures'],
  WORK_ENVIRONMENT: ['riskMatrix', 'actionPlan', 'inspectionReport'],
};

/** «2 av 4» */
export const progressOf = (checklist: Array<{ done: boolean }>) => ({
  done: checklist.filter((item) => item.done).length,
  total: checklist.length,
});

/** The point is on the list for the goal, and not done yet */
export const isOpen = (onboarding: { checklist: Array<{ key: string; done: boolean }> }, key: string) =>
  onboarding.checklist.some((item) => item.key === key && !item.done);

/** The point to do next: the first that is not done */
export const nextIndex = (checklist: Array<{ done: boolean }>) => checklist.findIndex((item) => !item.done);

/** The guidance in «Opprett team» and «Ny undersøkelse», read from the address */
export const guidanceFrom = (params: URLSearchParams): { goal: OnboardingGoal } | null => {
  const goal = params.get('goal');
  return params.get('onboarding') === '1' && (goal === 'TEAM_SAFETY' || goal === 'WORK_ENVIRONMENT') ? { goal } : null;
};

/** «Ny undersøkelse» with the guidance, for the team when it is known */
export const surveyStepPath = (goal: OnboardingGoal, teamId?: number) =>
  `${Routes.CreateSurvey}?onboarding=1&goal=${goal}${teamId ? `&teamId=${teamId}` : ''}`;

/** The survey type of the goal, as the form names it */
export const surveyTypeFor = (goal: OnboardingGoal) => (goal === 'WORK_ENVIRONMENT' ? 'work-environment' : 'psychological-safety');

/** «Trygghet høst 2026»: the season and year of the date */
export const seasonOf = (date: Date) => {
  const month = date.getMonth();
  return month <= 1 || month === 11 ? 'winter' : month <= 4 ? 'spring' : month <= 7 ? 'summer' : 'autumn';
};

/** What the home says when the user comes back from the first step */
export type FirstStepResult = { title: string; team: string; planned: boolean };

/** «Legg til verneombud» from «Kom i gang»: the invitation with the role, and the team when there is only one */
export const inviteSafetyRepPath = (teamId: number | null | undefined) => {
  const params = new URLSearchParams({ rolle: 'verneombud' });
  if (teamId) params.set('team', String(teamId));
  params.set('fra', 'kom-i-gang');
  return `${Routes.OrganizationInviteUser}?${params}`;
};

/** The survey the risk is assessed on */
export const surveyPath = (surveyId: number) => Routes.DashboardWorkEnvironmentSurvey.replace(':surveyId', String(surveyId));

/** The risk of the survey is calculated, or too few answered, so there is something to assess */
export const isAssessable = (survey: { state: 'collecting' | 'ready' | 'insufficient' } | null) =>
  survey !== null && survey.state !== 'collecting';

/**
 * Where each point of «Kom i gang» is done, for the menu and the next step alike. Only an administrator can
 * invite, so others add the safety representative on HR's page. The account has nowhere to go.
 */
export const checklistLinks = (
  onboarding: Pick<UserOnboarding, 'goal' | 'workEnvironment'>,
  mayInvite: boolean,
): Partial<Record<string, string>> => {
  const goal = onboarding.goal;
  const assessSurvey = onboarding.workEnvironment?.assessSurvey ?? null;
  return {
    riskMatrix: Routes.HRRiskMatrixFullPath,
    answers: Routes.CurrentSurveys,
    results: Routes.DashboardPsychologicalSafety,
    safetyRep: mayInvite ? inviteSafetyRepPath(onboarding.workEnvironment?.soleTeamId) : Routes.HRSafetyRepsFullPath,
    ...(goal && { firstMapping: firstStepPath(goal), firstSurvey: firstStepPath(goal) }),
    // The survey shows that the risk to assess appears there. Without one, a survey is made first.
    ...(assessSurvey ? { assessRisk: surveyPath(assessSurvey.id) } : goal && { assessRisk: surveyStepPath(goal) }),
  };
};
